import { Spin } from "antd";
import { isEqual } from "lodash";
import toNumber from "lodash/toNumber";
import { AlertCircle, Box, Package, Search, SearchIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDebounceValue } from "~/hooks/use-debounce";
import { useGetInventoriesInfinite } from "~/services/inventory";
import { TInventory } from "~/types";
import { capitalize, formatDimensions } from "~/utils";
import { CheckType, InventoryStatusCode } from "~/utils/constants";
import { useConsolidateStore } from "./consolidate.store";

export const SelectPieces = () => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const observerRef = useRef<HTMLDivElement | null>(null);

  const [debouncedSearch] = useDebounceValue(searchTerm, 500);

  const {
    clientId,
    selectedContainer,
    selectedPieces,
    setSelectedPieces,
    setListPieces,
    listPieces,
  } = useConsolidateStore();

  const {
    data: pieces,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetInventoriesInfinite({
    checkParentId: CheckType.CHECK,
    search: debouncedSearch,
    clientId,
  });

  const allPieces = useMemo(
    () => pieces?.pages.flatMap((page) => page.items) ?? [],
    [pieces]
  );

  useEffect(() => {
    if (allPieces.length > 0 && !isEqual(listPieces, allPieces)) {
      setListPieces(allPieces);
    }
  }, [allPieces, listPieces, setListPieces]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 1.0 }
    );

    const currentRef = observerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleFilterClick = () => {
    setSearchTerm(searchInput);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setSearchTerm(searchInput);
    }
  };

  const isPieceCompatible = (piece: TInventory) => {
    if (!selectedContainer) return true;

    const container = selectedContainer;
    return (
      toNumber(piece.length) <= toNumber(container.length) &&
      toNumber(piece.width) <= toNumber(container.width) &&
      toNumber(piece.height) <= toNumber(container.height)
    );
  };

  // All consolidated pieces must share the same status
  const isPieceStatusCompatible = (piece: TInventory) => {
    if (selectedPieces.length === 0) return true;
    return piece.status === selectedPieces[0].status;
  };

  const getIncompatibilityReason = (piece: TInventory) => {
    if (!isPieceCompatible(piece))
      return "This piece is too large for the selected container";
    if (!isPieceStatusCompatible(piece))
      return `Status mismatch — selected pieces are "${capitalize(selectedPieces[0]?.status)}"`;
    return null;
  };

  const togglePieceSelection = (piece: TInventory) => {
    if (selectedPieces.some((p) => p.id === piece.id)) {
      setSelectedPieces(
        selectedPieces.filter((p) => p.id !== piece.id)
      );
    } else {
      setSelectedPieces([...selectedPieces, piece]);
    }
  };

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      <div className="p-4 border-b">
        <h2 className="font-medium">Select Pieces</h2>
        <p className="text-sm text-gray-500 mt-1">
          Choose the pieces to consolidate into {selectedContainer?.title} (
          {selectedContainer?.inventoryRef})
        </p>
      </div>

      <div className="p-4 border-b">
        <div className="flex items-center space-x-2">
          <div className="relative flex-1">
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
              placeholder="Search pieces..."
              value={searchInput}
              onChange={handleSearchInputChange}
              onKeyDown={handleKeyDown}
            />
            <Search
              className="absolute left-3 top-2.5 text-gray-400"
              size={16}
            />
          </div>
          <button
            className="px-3 py-2 border rounded-lg hover:bg-gray-50 flex items-center space-x-1"
            onClick={handleFilterClick}
          >
            <SearchIcon size={16} />
            <span>Search</span>
          </button>
        </div>

        {/* Container info summary */}
        <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
          <div className="flex items-center">
            <Box size={16} className="text-blue-600 mr-2 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-medium">
                {selectedContainer?.title} ({selectedContainer?.inventoryRef})
              </div>
              <div className="text-sm text-gray-600">
                {formatDimensions(selectedContainer)}
              </div>
            </div>
            <div className="text-sm text-gray-600">
              {selectedContainer?.location?.name}
            </div>
          </div>
        </div>
      </div>

      <Spin spinning={isLoading && allPieces.length === 0}>
        {allPieces.length > 0 ? (
          <div className="divide-y max-h-96 overflow-y-auto">
            {allPieces
              .filter((piece: TInventory) => piece.id !== selectedContainer?.id)
              .map((piece: TInventory) => {
                const incompatibilityReason = getIncompatibilityReason(piece);
                const isSelectable = !incompatibilityReason || selectedPieces.some((p) => p.id === piece.id);
                return (
                  <div
                    key={piece.id}
                    className={`p-4 ${
                      !isSelectable
                        ? "bg-gray-50 opacity-70"
                        : selectedPieces.some((p) => p.id === piece.id)
                        ? "bg-blue-50"
                        : "hover:bg-gray-50"
                    } cursor-pointer`}
                    onClick={() => isSelectable && togglePieceSelection(piece)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex items-start space-x-3">
                        <div
                          className={`p-2 rounded-lg ${
                            !isSelectable
                              ? "bg-gray-100"
                              : selectedPieces.some((p) => p.id === piece.id)
                              ? "bg-blue-100"
                              : "bg-gray-100"
                          }`}
                        >
                          <Package
                            size={20}
                            className={
                              !isSelectable
                                ? "text-gray-400"
                                : selectedPieces.some((p) => p.id === piece.id)
                                ? "text-blue-600"
                                : "text-gray-600"
                            }
                          />
                        </div>
                        <div>
                          <div className="font-medium">{piece.title}</div>
                          <div className="text-sm text-gray-500">
                            {piece.inventoryRef}
                          </div>
                          <div className="flex items-center mt-1 space-x-3">
                            <div className="text-sm">{piece.artistName}</div>
                            <div className="text-sm text-gray-500">
                              {formatDimensions(piece)}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">
                          {piece.client
                            ? piece.client.companyName || "Unnamed Client"
                            : "No client"}
                        </div>
                        <div className="text-sm text-gray-500">
                          {piece.location?.name}
                        </div>
                        <div className="text-xs mt-1">
                          <span
                            className={`px-2 py-1 rounded-full ${
                              piece.status ===
                              InventoryStatusCode.AVAILABLE_ON_HAND
                                ? "bg-green-100 text-green-800"
                                : "bg-amber-100 text-amber-800"
                            }`}
                          >
                            {capitalize(piece.status)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {incompatibilityReason && !selectedPieces.some((p) => p.id === piece.id) && (
                      <div className="mt-2 p-2 bg-amber-50 border border-amber-100 rounded text-sm text-amber-700 flex items-center">
                        <AlertCircle size={14} className="mr-1" />
                        <span>{incompatibilityReason}</span>
                      </div>
                    )}

                    {piece.specialHandling &&
                      piece.specialHandling.length > 0 && (
                        <div className="mt-2">
                          <div className="text-sm text-gray-500">
                            Special Handling:
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {piece.specialHandling.map(
                              (handling: string, idx: number) => (
                                <span
                                  key={idx}
                                  className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs"
                                >
                                  {capitalize(handling)}
                                </span>
                              )
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                );
              })}

            {/* Infinite scroll trigger */}
            <div ref={observerRef} className="p-4 text-center">
              {isFetchingNextPage && <Spin className="my-2" />}
              {!hasNextPage && allPieces.length > 0 && (
                <p className="text-gray-500 text-sm">No more pieces to load</p>
              )}
            </div>
          </div>
        ) : (
          <div className="p-8 text-center">
            <Package size={40} className="mx-auto mb-3 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-700">
              {isLoading ? "Loading pieces..." : "No pieces found"}
            </h3>
            <p className="text-gray-500 mt-1">
              {debouncedSearch
                ? "Try adjusting your search terms"
                : "There are no available pieces to consolidate"}
            </p>
          </div>
        )}
      </Spin>
    </div>
  );
};
